import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Heart } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { HERO_MURAL } from "@/lib/constants";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]";

export default function CustomerAuth() {
  const { user, loading, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("register");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && user) nav(user.role === "customer" ? "/account" : "/"); }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      let u;
      if (mode === "register") u = await register({ ...f, account_type: "customer" });
      else u = await login(f.email, f.password);
      nav(u?.role === "customer" ? "/account" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <Seo title="My RiNo Account" description="Follow your favorite RiNo venues, get event and deal alerts, and plan your visits." />
      <div className="hidden lg:block relative grain">
        <img src={HERO_MURAL} alt="RiNo" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10">
          <h2 className="font-display font-black text-4xl text-white">Your RiNo, your way.</h2>
          <p className="text-neutral-300 mt-3">Follow venues, live music, wine & beer, and get only the alerts you want — how and when you want them.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 text-[#FF0055] mb-2"><Heart size={20} /> <span className="font-accent text-sm uppercase tracking-widest">Visitor Account</span></div>
          <h1 className="font-display font-extrabold text-3xl text-white">{mode === "register" ? "Join Visit RiNo" : "Welcome back"}</h1>
          <p className="text-neutral-400 text-sm mt-2 mb-6">{mode === "register" ? "Free — follow places, save favorites, and get personalized alerts." : "Sign in to your visitor account."}</p>
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && <input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cust-name" className={inputCls} />}
            <input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="cust-email" className={inputCls} />
            <input required type="password" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="cust-password" className={inputCls} />
            {error && <p className="text-[#FF0055] text-sm" data-testid="cust-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="cust-submit" className="w-full bg-[#FF0055] hover:bg-[#ff2d74] rounded-none h-11 font-semibold">
              {busy ? <Loader2 size={16} className="animate-spin" /> : (mode === "register" ? "Create account" : "Sign in")}
            </Button>
          </form>
          <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="cust-toggle-mode" className="text-neutral-400 text-sm mt-4 hover:text-white">
            {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
          </button>
          <p className="text-neutral-600 text-xs mt-6">Run a business or tours? <Link to="/owner" className="text-[#00E5FF]">Use the business portal →</Link></p>
        </div>
      </div>
    </div>
  );
}
